import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '@/App';
import { axios, API, authHdr, downloadFile } from './opsApi';
import { toast } from 'sonner';
import { FileBarChart2, FileText, FileSpreadsheet, Printer } from 'lucide-react';

function OpsReports() {
  const { token } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const [kind, setKind] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/ops-reports/meta`, authHdr(token)); setMeta(res.data); }
      catch { setMeta({ available: [], can_export: false }); }
    })();
  }, [token]);

  const openReport = async (k) => {
    setKind(k); setReport(null); setLoading(true);
    try { const res = await axios.get(`${API}/ops-reports/${k}/preview`, authHdr(token)); setReport(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load report'); } finally { setLoading(false); }
  };

  const doExport = async (fmt) => {
    try { await downloadFile(`${API}/ops-reports/${kind}/export?format=${fmt}`, token, `ops-${kind}.${fmt === 'xlsx' ? 'xlsx' : 'pdf'}`); }
    catch (e) { toast.error(e?.response?.status === 403 ? 'Export permission required' : 'Export failed'); }
  };

  if (!meta) return <div className="p-8 text-center text-sm text-slate-400">Loading…</div>;
  if ((meta.available || []).length === 0) {
    return <div className="p-8"><div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-400" data-testid="ops-reports-none">You do not have reporting access, or no report sections are permitted for you.</div></div>;
  }

  return (
    <div className="p-6" data-testid="ops-reports">
      <h2 className="mb-1 flex items-center gap-2 text-2xl font-bold text-[#0E2A47]"><FileBarChart2 size={22} />Operations Reports</h2>
      <p className="mb-5 text-sm text-slate-500">Reports honor your section permissions and location scope.</p>

      <div className="mb-5 flex flex-wrap gap-2" data-testid="ops-reports-picker">
        {meta.available.map((r) => (
          <button key={r.kind} onClick={() => openReport(r.kind)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${kind === r.kind ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`ops-report-pick-${r.kind}`}>{r.title}</button>
        ))}
      </div>

      {loading && <div className="py-10 text-center text-sm text-slate-400" data-testid="ops-reports-loading">Building report…</div>}

      {report && !loading && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="ops-report-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 print:hidden">
            <div>
              <div className="font-bold text-[#0E2A47]">{report.customer_name}</div>
              <div className="text-xs text-slate-500">Prepared by {report.prepared_by} · {String(report.generated_at).slice(0, 10)}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => window.print()} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600" data-testid="ops-report-print"><Printer size={14} />Print</button>
              {meta.can_export && <>
                <button onClick={() => doExport('pdf')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600" data-testid="ops-report-export-pdf"><FileText size={14} />PDF</button>
                <button onClick={() => doExport('xlsx')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600" data-testid="ops-report-export-xlsx"><FileSpreadsheet size={14} />Excel</button>
              </>}
            </div>
          </div>

          <div className="p-5">
            {(report.sections || []).map((s, i) => (
              <div key={i} className="mb-6" data-testid={`ops-report-section-${i}`}>
                <div className="mb-2 rounded bg-[#0E2A47] px-3 py-1.5 text-sm font-bold text-white">{s.label}</div>
                {s.gap ? (
                  <div className="px-2 py-3 text-sm text-amber-600">{s.gap_message}</div>
                ) : s.type === 'table' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                        {s.headers.map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {s.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-slate-50">
                            {row.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-[#0E2A47]">{String(c)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {s.note && <div className="mt-2 text-xs italic text-slate-400">{s.note}</div>}
              </div>
            ))}
            {(report.gaps || []).length > 0 && (
              <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="ops-report-gaps">
                {report.gaps.map((g, i) => <div key={i}>• {g}</div>)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OpsReports;
